import { AxiosResponse } from 'axios';

import config from '../../config/config';
import {
  AdhocRemoteStartPayload,
  AdhocRemoteStopPayload,
  AdhocSessionPayload,
  EstimateApiResponse,
  EstimateInterface,
  QueryParameters,
} from '../../interface/charger.interface';
import getFirstSubdomain from '../../shared-functions/DomiansplitFunction';
import { getLocalStorageItem } from '../../shared-functions/LocalStorage';

import getRequest from './base/get';
import postRequest, { PostRequestOptions } from './base/post';
import BrandingService from './branding';

export type {
  AdhocRemoteStartPayload,
  AdhocRemoteStopPayload,
  AdhocSessionPayload,
  EstimateApiResponse,
  EstimateInterface,
  QueryParameters,
};

const charger = {
  /**
   * Create Adhoc Session Token
   * Endpoint: POST /emsp/api/v1/adhoc/session
   * @param data Optional session payload (evseUid, connectorId)
   */
  createAdhocSession: (data?: AdhocSessionPayload): Promise<any> => {
    return postRequest(`${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/session`, data);
  },

  /**
   * Adhoc Remote Start Charging
   * Endpoint: /emsp/api/v1/adhoc/remote-start
   * @param data - Request payload object.
   * @returns Promise resolving to remote start response.
   */
  adhocRemoteStart: (data: AdhocRemoteStartPayload): Promise<any> => {
    return postRequest(`${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/remote-start`, data);
  },

  /**
   * Get Adhoc Command Status
   * Endpoint: GET /emsp/api/v1/adhoc/command/:command_id/status
   * @param commandId Command identifier
   * @returns Command status response
   */
  getCommandStatus: (commandId: string | number): Promise<any> => {
    return getRequest(
      `${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/command/${commandId}/status`,
    );
  },

  /**
   * Fetch Live Charging Session Data
   * Endpoint: POST /sessions/api/emsp/v2/adhoc/live
   * @param data Optional request payload
   */
  fetchCommercialTransaction: (data: any = {}): Promise<any> => {
    return postRequest(`${process.env.REACT_APP_API_URL}sessions/api/emsp/v2/adhoc/live`, data);
  },

  /**
   * Fetch location by EVSE ID
   * Endpoint: /emsp/api/v1/adhoc/locations/by-evse/:evseId
   * @param evseId EVSE identifier
   */
  fetchLocationByEvse: (evseId: string | number) => {
    const cleanEvseId = String(evseId).split('?')[0].split('&')[0];
    return getRequest(
      `${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/locations/by-evse/${cleanEvseId}`,
    );
  },

  /**
   * Fetch tariff details by EVSE ID
   * Endpoint: /emsp/api/v1/adhoc/tariffs/by-evse/:evseId
   * @param evseId EVSE identifier
   */
  fetchTariffByEvse: (evseId: string | number) => {
    const cleanEvseId = String(evseId).split('?')[0].split('&')[0];
    return getRequest(
      `${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/tariffs/by-evse/${cleanEvseId}`,
    );
  },

  /**
   * Fetch a device (alias to fetchLocationByEvse)
   * @param deviceSN Device serial number
   */
  fetchOneDevicePoints: (deviceSN: string | number) => {
    const cleanDeviceSN = String(deviceSN).split('?')[0].split('&')[0];
    return getRequest(
      `${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/locations/by-evse/${cleanDeviceSN}`,
    );
  },

  /**
   * Generate Temp Token to start charging
   */
  generateTempToken: () => {
    return getRequest(`${config.ocppApiUrl}api/ocpp16/v1/guest/generate-guest-token`);
  },

  /**
   * Adhoc Remote Stop Charging
   * Endpoint: POST /emsp/api/v1/adhoc/remote-stop
   * @param data - Request payload ({ sessionId }).
   * @returns Promise resolving to remote stop response.
   */
  stopCharging: (data: AdhocRemoteStopPayload = {}): Promise<any> => {
    const payload: AdhocRemoteStopPayload = {
      sessionId: data.sessionId || getLocalStorageItem('sessionId') || undefined,
      ...data,
    };
    delete payload.adhocUid;
    return postRequest(`${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/remote-stop`, payload);
  },

  /**
   * Adhoc Remote Stop Charging (alias)
   * Endpoint: POST /emsp/api/v1/adhoc/remote-stop
   * @param data - Request payload ({ sessionId }).
   * @returns Promise resolving to remote stop response.
   */
  adhocRemoteStop: (data: AdhocRemoteStopPayload = {}): Promise<any> => {
    return charger.stopCharging(data);
  },

  /**
   * Get transaction
   * Endpoint: POST /sessions/api/emsp/v2/adhoc/live
   * @param data - Request payload object.
   * @returns Promise resolving to live transaction response.
   */
  getTransaction: (data: any = {}): Promise<any> => {
    return postRequest(`${process.env.REACT_APP_API_URL}sessions/api/emsp/v2/adhoc/live`, data);
  },

  /**
   * Function to refresh charging session
   * @param data - Request payload or parameters.
   * @returns Promise resolving to charging transaction details.
   */
  refreshSession: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/guest/v1/charging-transaction`, data);
  },

  /**
   * Return Api call
   * @param query - HyperPay registration identifier query string.
   * @returns Promise resolving to registration status response.
   */
  getReturnApiCall: (query: string): Promise<any> => {
    const domain = getFirstSubdomain();
    const brandData = BrandingService.getCachedBranding();
    return getRequest(
      `${domain && brandData?.transactionApiUrl ? brandData?.transactionApiUrl : process.env.REACT_APP_TRANSACTION_API_URL}api/end-consumer/v1/hyperpay-payment/registration-status/${query}`,
      {},
    );
  },

  /**
   * Fetch Idle Fee
   * @param query - Query parameters for penalty management.
   * @returns Promise resolving to penalty management data.
   */
  fetchIdleFee: (query: QueryParameters): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/guest/v1/penalty-management`, query);
  },

  /**
   * Fetch active session
   * @param userCustomId - End-consumer user identifier string.
   * @param query - Query parameters for active session lookup.
   * @returns Promise resolving to active session details.
   */
  fetchActiveSession: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/guest/v1/end-consumer/get-active-session/${userCustomId}`,
      query,
    );
  },

  /**
   * Fetch Session Summary / CDR by Session ID
   * Endpoint: GET /transactions/api/driver/v1/adhoc-cdrs/session/:sessionId
   * @param sessionId Session identifier
   */
  fetchSessionCdr: (sessionId: string): Promise<any> => {
    return getRequest(
      `${process.env.REACT_APP_API_URL}transactions/api/driver/v1/adhoc-cdrs/session/${sessionId}`,
    );
  },

  /**
   * Fetch Tax Invoice by CDR ID
   * Endpoint: POST /transactions/api/driver/v1/adhoc-cdrs/:cdrId/invoice
   * @param cdrId CDR identifier
   * @param data Invoice request payload
   * @param options Optional request options
   */
  fetchCdrInvoice: (cdrId: string, data?: any, options?: PostRequestOptions): Promise<any> => {
    return postRequest(
      `${process.env.REACT_APP_API_URL}transactions/api/driver/v1/adhoc-cdrs/${cdrId}/invoice`,
      data,
      false,
      { skipGuestToken: true, ...options },
    );
  },

  /**
   * Fetch Adhoc Charging Session Estimate
   * Endpoint: POST /emsp/api/v1/adhoc/estimate
   * @param data Request payload
   * @returns Estimate API response
   */
  getAdhocEstimate: (
    data: EstimateInterface,
  ): Promise<AxiosResponse<EstimateApiResponse | { data: EstimateApiResponse }>> => {
    return postRequest(`${process.env.REACT_APP_API_URL}emsp/api/v1/adhoc/estimate`, data);
  },
};

export default charger;
